import {
  Component,
  OnInit,
  OnChanges,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { PackageService } from '../../services/package.service';
import { Package } from '../../shared/types';
import { hasDisplayStrikeoffPrice } from '../../shared/utils/strikeoff-price.util';

interface SelectablePackage extends Package {
  selected?: boolean;
}

/** The only wash type the Add Appointment flow books. */
const APPOINTMENT_WASH_TYPE = 'detailing';

/**
 * Step 1 of the Add Appointment modal: pick a detailing package.
 *
 * The component fetches its own list and never reads `PackageService.packages$`.
 * That shared cache is filled by whichever page loaded last (the Appointments page
 * loads every wash type for its filter modal), so rendering it first showed an
 * unrelated package, auto-selected it, and then swapped in the detailing list a
 * moment later (CWS-595). Owning the fetch also keeps this picker from overwriting
 * the cache other components are displaying.
 */
@Component({
  selector: 'app-package-selection',
  templateUrl: './package-selection.component.html',
  styleUrls: ['./package-selection.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule],
})
export class PackageSelectionComponent implements OnInit, OnChanges, OnDestroy {
  @Input() selectedPackageId = '';
  @Output() packageSelected = new EventEmitter<string>();

  /** Active detailing packages with the current selection flagged. */
  packages: SelectablePackage[] = [];
  /** True until the first fetch settles; gates the empty state so it cannot flash. */
  loading = true;

  private loadSubscription?: Subscription;

  constructor(
    private packageService: PackageService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.loadSubscription = this.packageService
      .getPackagesByWashTypes(APPOINTMENT_WASH_TYPE)
      .subscribe(packages => {
        this.packages = packages
          .filter(p => p.package_status === true)
          .map(p => ({ ...p, selected: false }));
        this.loading = false;

        // Keep the parent's choice when it is on screen; otherwise default to the
        // first package and tell the parent, so it never holds an id the user cannot see.
        const resolvedId = this.packages.some(p => p.id === this.selectedPackageId)
          ? this.selectedPackageId
          : (this.packages[0]?.id ?? '');
        this.applySelection(resolvedId);
        if (resolvedId && resolvedId !== this.selectedPackageId) {
          this.packageSelected.emit(resolvedId);
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // After load the parent is the source of truth; an id that is not in the list
    // simply leaves nothing selected (Continue stays disabled upstream).
    if (changes['selectedPackageId'] && !changes['selectedPackageId'].firstChange) {
      this.applySelection(this.selectedPackageId);
    }
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  hasStrikeoffPrice(value: unknown): boolean {
    return hasDisplayStrikeoffPrice(value);
  }

  /**
   * Staff enter the price for these at checkout, so the package holds none. The API sends the
   * flag as a boolean; older payloads use 1 / '1' / 'true', which the package editor accepts
   * too, so read all four rather than trusting the type.
   */
  isCustomPricePackage(selectablePackage: SelectablePackage): boolean {
    const flag = selectablePackage?.allows_custom_price as unknown;
    return flag === true || flag === 1 || flag === '1' || flag === 'true';
  }

  selectPackage(selectedPackage: SelectablePackage): void {
    this.applySelection(selectedPackage.id);
    this.packageSelected.emit(selectedPackage.id);
  }

  createPackage(): void {
    // Navigate to the package route with a query parameter to open the modal
    this.router.navigate(['/package'], { queryParams: { openModal: 'true' } });
  }

  trackByIndex(index: number): number {
    return index;
  }

  trackById(_: number, item: SelectablePackage): string {
    return item?.id;
  }

  private applySelection(id: string): void {
    this.packages = this.packages.map(p => ({ ...p, selected: p.id === id }));
  }
}
